import React, { useCallback, useMemo, useState } from "react";

import { bem } from "@react-md/utils";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import { EntityTypeSupportingPinnedFields } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { CategorizedFields } from "@mapmycustomers/shared/types/fieldModel/IFieldModel";

import { useConfigProvider } from "../ConfigProvider";
import SearchField from "../SearchField";

const block = bem("mmc-entity-field-selector-fields-list");

interface EntityFieldsListProps {
  editableFields?: Partial<CategorizedFields>;
  entityType: EntityTypeSupportingPinnedFields;
  onChange?: (field: IField, entityType: EntityTypeSupportingPinnedFields) => void;
  onCloseDropdown: () => void;
}

const EntityFieldsList = ({
  editableFields,
  entityType,
  onChange,
  onCloseDropdown,
}: EntityFieldsListProps) => {
  const [search, setSearch] = useState("");

  const { formatMessage, getFieldCategoryDisplayName } = useConfigProvider();
  const filteredCategories = useMemo(
    () =>
      editableFields
        ? (Object.keys(editableFields) as FieldCategory[]).filter(
            (category) => editableFields[category]?.length
          )
        : [],
    [editableFields]
  );

  const handleFieldClick = useCallback(
    (field: IField) => {
      onChange?.(field, entityType);
      onCloseDropdown();
    },
    [entityType, onChange, onCloseDropdown]
  );

  if (!editableFields) {
    return null;
  }

  return (
    <div className={block()}>
      <SearchField
        onChange={setSearch}
        placeholder={formatMessage("ui.entityFieldSelector.search.placeholder")}
        value={search}
      />
      <div className={block("entity-fields")}>
        {filteredCategories.map((category) => {
          const filteredFields = editableFields[category]?.filter(
            (field) =>
              !search ||
              field.displayName.trim().toLowerCase().includes(search.trim().toLowerCase())
          );
          if (!filteredFields || !filteredFields.length) {
            return;
          }

          return (
            <React.Fragment key={category}>
              <span className={block("field-category")} key={category}>
                {getFieldCategoryDisplayName(category)}
              </span>
              {filteredFields?.map((field) => (
                <span
                  className={block("field-item")}
                  key={field.name}
                  onClick={() => handleFieldClick(field)}
                >
                  {field.displayName}
                </span>
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default EntityFieldsList;
